import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Building2, Calendar, ChevronDown, ExternalLink, MapPin } from 'lucide-react';

const ExperienceCard = ({ experience, isExpanded, onToggle }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="bg-gradient-to-br from-[#101C10]/40 to-[#112811]/40 backdrop-blur-sm 
                 rounded-2xl border border-[#2fe22f]/10 hover:border-[#2fe22f]/30 
                 transition-all duration-500 overflow-hidden"
    >
      {/* Header Section - Always Visible */}
      <div 
        className="p-8 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex justify-between items-start mb-4">
          <div className="space-y-2">
            <h3 className="text-2xl font-bold text-[#2fe22f]">{experience.title}</h3>
            <div className="flex items-center gap-2 text-gray-300">
              <Building2 className="w-4 h-4" />
              <span>{experience.company}</span>
            </div>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <ChevronDown className="w-6 h-6 text-[#2fe22f]" />
          </motion.div>
        </div>

        <div className="flex flex-wrap gap-4 text-sm text-gray-300">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>{experience.duration}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            <span>{experience.location}</span>
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      <motion.div
        animate={{ height: isExpanded ? "auto" : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="px-8 pb-8 space-y-6">
          <div className="h-px bg-gradient-to-r from-transparent via-[#2fe22f]/20 to-transparent" />
          
          {/* Key Achievements */}
          <div className="space-y-4">
            <h4 className="text-xl font-semibold text-[#2fe22f]/90">Key Achievements</h4>
            <ul className="space-y-3">
              {experience.achievements.map((achievement, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-3 text-gray-300"
                >
                  <div className="mt-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2fe22f]" />
                  </div>
                  {achievement}
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Technologies Used */}
          <div className="space-y-4">
            <h4 className="text-xl font-semibold text-[#2fe22f]/90">Technologies Used</h4>
            <div className="flex flex-wrap gap-2">
              {experience.technologies.map((tech) => (
                <span 
                  key={tech}
                  className="px-4 py-2 text-sm bg-[#2fe22f]/10 text-[#2fe22f] rounded-full"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* Additional Details */}
          {experience.details && (
            <div className="space-y-4">
              <h4 className="text-xl font-semibold text-[#2fe22f]/90">Additional Details</h4>
              <p className="text-gray-300 leading-relaxed">{experience.details}</p>
            </div>
          )}

          {/* Projects & Links */}
          {experience.projects && (
            <div className="space-y-4">
              <h4 className="text-xl font-semibold text-[#2fe22f]/90">Notable Projects</h4>
              <div className="space-y-3">
                {experience.projects.map((project, index) => (
                  <a
                    key={index}
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-gray-300 hover:text-[#2fe22f] transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>{project.name}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const Experience = () => {
  const [expandedId, setExpandedId] = useState(null);

  const experiences = [
    {
      id: 1,
      title: "Frontend Developer Intern",
      company: "Margdarshan",
      duration: "June 2024 - August 2024",
      location: "Remote",
      achievements: [
        "Developed UI components using React for the newly revamped company website.",
        "Integrated the frontend with the backend, ensuring seamless communication and data flow.",
        "Implemented Razorpay payment gateway to facilitate secure online transactions",
      ],
      technologies: ["React", "Node.js", "Python", "TailwindCSS", "Razaorpay"]
    },
    {
      id: 2,
      title: "Graphic Design Lead",
      company: "Algozenith CRCE",
      duration: "June 2023 - May 2024",
      location: "Mumbai, IN",
      achievements: [
        "Designed and Developed the website for AlgoZenith CRCE: https://algozenithcrce.vercel.app/",
        "Conducted Tech Vista, a coding competition held in collaboration with IEEE CRCE",
      ],
      technologies: ["React","Firebase","Vanilla CSS","Vercel","Canva"],
    },
  ];

  return (
    <section className="w-full font-lato min-h-screen bg-gradient-to-br from-[#101C10] to-[#112811] py-20" id='experience'>
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="container mx-auto px-6 max-w-5xl"
      >
        {/* Section Header */}
        <div className="flex items-center gap-4 mb-16">
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="w-12 h-1 bg-[#2fe22f]" 
          />
          <h2 className="text-4xl font-bold text-[#2fe22f]">Experience</h2>
        </div>

        {/* Experience Cards */}
        <div className="space-y-6">
          {experiences.map((experience) => (
            <ExperienceCard
              key={experience.id}
              experience={experience}
              isExpanded={expandedId === experience.id}
              onToggle={() => setExpandedId(expandedId === experience.id ? null : experience.id)}
            />
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default Experience;